import React from "react";
import { motion } from "framer-motion";
import {
  Send,
  MessageSquare,
  Search,
  BadgeCheck,
  GraduationCap,
  TrendingUp,
} from "lucide-react";

/* =====================================================================
   DEALER JOURNEY — Premium 6-step horizontal timeline.
   Desktop (lg+): 6-column grid with a single orange line connecting the
   icon row.
   Tablet/Mobile (<lg): vertical stack with a left-side orange line
   running through all steps. Equal spacing, lightweight scroll-in fade
   animations, subtle hover lift on each icon.
   ===================================================================== */

const STEPS = [
  {
    id: 1,
    icon: Send,
    title: "Submit Application",
    desc: "Complete the dealership enquiry form with your business details.",
  },
  {
    id: 2,
    icon: MessageSquare,
    title: "Initial Discussion",
    desc: "Our team will contact you to understand your business profile and requirements.",
  },
  {
    id: 3,
    icon: Search,
    title: "Application Review",
    desc: "We evaluate your business, market potential and long-term partnership opportunities.",
  },
  {
    id: 4,
    icon: BadgeCheck,
    title: "Dealer Approval",
    desc: "Once approved, you officially become a LIFTILE Dealer.",
  },
  {
    id: 5,
    icon: GraduationCap,
    title: "Product & Sales Training",
    desc: "Receive product knowledge, technical guidance and marketing support from our team.",
  },
  {
    id: 6,
    icon: TrendingUp,
    title: "Grow With LIFTILE",
    desc: "Start building your business with premium construction machinery backed by engineering excellence and continuous support.",
  },
];

export default function DealerJourney() {
  return (
    <section
      data-testid="dealer-journey"
      className="relative bg-liftile-matte-black py-24 md:py-32 overflow-hidden"
    >
      {/* Top thin orange accent + soft ambient halo (matches Expanding Presence rhythm) */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30" />
      <div
        aria-hidden
        className="absolute top-[60%] left-1/2 -translate-x-1/2 -translate-y-1/2 w-[820px] h-[820px] rounded-full pointer-events-none opacity-[0.08]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-3xl mx-auto text-center"
        >
          <div className="heading-eyebrow mb-4">Dealer Journey</div>
          <h2
            data-testid="dealer-journey-heading"
            className="font-display uppercase text-liftile-cream leading-[1.02] tracking-tight text-4xl sm:text-5xl md:text-[3.25rem]"
          >
            Become a LIFTILE Dealer in{" "}
            <span className="text-liftile-orange">6 Simple Steps</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed mx-auto max-w-2xl">
            Our dealership process is designed to be transparent, efficient and
            focused on building long-term business partnerships.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="mt-16 md:mt-24 relative">
          {/* Desktop horizontal connecting line (sits behind the icon row) */}
          <div
            aria-hidden
            className="hidden lg:block absolute left-0 right-0 top-[42px] h-px"
          >
            <div className="mx-auto max-w-[92%] h-full bg-gradient-to-r from-transparent via-liftile-orange/55 to-transparent" />
          </div>

          {/* Mobile/tablet vertical connecting line */}
          <div
            aria-hidden
            className="lg:hidden absolute left-[42px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-liftile-orange/55 to-transparent"
          />

          <ol className="relative grid grid-cols-1 lg:grid-cols-6 gap-y-12 lg:gap-x-6">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.li
                  key={step.id}
                  data-testid={`dealer-journey-step-${step.id}`}
                  initial={{ opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.6,
                    delay: i * 0.08,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="group relative flex lg:flex-col items-start lg:items-center gap-5 lg:gap-0 lg:text-center"
                >
                  {/* Icon node */}
                  <div className="relative shrink-0">
                    <div className="w-[84px] h-[84px] rounded-full bg-liftile-black border border-white/10 flex items-center justify-center transition-all duration-500 group-hover:border-liftile-orange group-hover:-translate-y-1 group-hover:shadow-[0_18px_36px_rgba(255,107,26,0.28)]">
                      <Icon
                        className="w-8 h-8 text-liftile-orange transition-transform duration-500 group-hover:scale-110"
                        strokeWidth={1.5}
                      />
                    </div>
                    {/* Soft glow on hover */}
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{
                        boxShadow:
                          "0 0 0 4px rgba(255,107,26,0.08), 0 0 28px rgba(255,107,26,0.25)",
                      }}
                    />
                  </div>

                  {/* Text block */}
                  <div className="flex-1 lg:mt-7">
                    <div className="font-heading text-[10px] uppercase tracking-[0.32em] text-liftile-orange font-semibold">
                      Step {String(step.id).padStart(2, "0")}
                    </div>
                    <h3 className="font-heading text-base md:text-[17px] uppercase tracking-wider font-semibold text-liftile-cream mt-2.5 leading-snug">
                      {step.title}
                    </h3>
                    <p className="font-body text-sm md:text-[14.5px] text-liftile-cream/65 mt-3 leading-relaxed lg:px-2">
                      {step.desc}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
